<template>
  <!-- Two layouts share one picker/list:
       • default — a full bar (Attach button, hint, drag/drop zone), used standalone (test bench).
       • compact — chips + hidden picker only; the parent composer owns the "+" trigger and drop
         zone and drives us via pickFiles()/addFiles()/addClipboardItems(). Collapses to nothing
         (only the hidden input) when there's nothing to show. -->
  <div
    class="tm-attach"
    :class="{ 'tm-attach--drag': dragOver, 'tm-attach--disabled': disabled, 'tm-attach--compact': compact }"
    v-show="!compact || value.length || error"
    @dragover="onDragOver"
    @dragenter="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div v-if="!compact" class="tm-attach-bar">
      <button type="button" class="tm-attach-btn" :disabled="disabled" @click="pickFiles">
        <font-awesome-icon :icon="['fas', 'paperclip']" />
        Attach
      </button>
      <span class="tm-attach-hint">Drag &amp; drop or paste — images &amp; files</span>
      <span v-if="value.length" class="tm-attach-summary">
        {{ value.length }} file{{ value.length === 1 ? '' : 's' }} · {{ formatBytes(totalBytes) }}
        <button type="button" class="tm-attach-clear" :disabled="disabled" @click="clearAll">Clear</button>
      </span>
    </div>

    <ul v-if="value.length" class="tm-attach-list">
      <li v-for="(att, i) in value" :key="att.id" class="tm-attach-item"
        :class="{ 'tm-attach-item--error': att.uploadError }">
        <img v-if="att.isImage && att.dataUrl" :src="att.dataUrl" class="tm-attach-thumb" :alt="att.name" />
        <span v-else class="tm-attach-ficon"><font-awesome-icon :icon="['far', 'file']" /></span>
        <span class="tm-attach-info">
          <span class="tm-attach-name" :title="att.name">{{ att.name }}</span>
          <span class="tm-attach-size">
            {{ formatBytes(att.size) }}
            <font-awesome-icon v-if="att.uploading" :icon="['fas', 'spinner']" spin title="Uploading…" />
            <font-awesome-icon v-else-if="att.uploadError" class="tm-attach-erricon"
              :icon="['fas', 'exclamation-circle']" title="Upload failed — this file won't be sent" />
          </span>
        </span>
        <button type="button" class="tm-attach-remove" :disabled="disabled" title="Remove" @click="removeAt(i)">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </li>
    </ul>

    <div v-if="error" class="tm-attach-error">{{ error }}</div>
    <input ref="fileInput" type="file" multiple class="tm-attach-file" @change="onInputChange" />
  </div>
</template>

<script>
/**
 * Reusable attachment picker for the AI Teammate surfaces.
 *
 * v-model is an array of attachment objects:
 *   { id, name, mimeType, size, isImage, dataUrl, data, path, uploading, uploadError }
 * where `data` is base64 (no data-URL prefix), `dataUrl` is the full data URL kept for
 * previewing, and `path` is the canonical "/s/{shortId}/{fileName}" S3 ref once the staged
 * upload (request-upload-url → PUT → confirm-upload) completes.
 *
 * Files are uploaded to S3 the moment they're added, so by send time the attachment is a
 * lightweight ref the model can also attach to entry File/Image fields. Small files keep
 * their base64 as a fallback for when the staged upload fails (S3 disabled, CORS, network);
 * large files have no fallback — a failed upload marks the chip and the send drops it.
 * attachmentsPayload (teammateChatMixin) prefers { name, path, mimeType, size } over
 * { name, mimeType, data }.
 *
 * Bitmap images over the autoCompress threshold are downscaled before any of that (same
 * compressorjs settings as the comment / file-upload fields), so the S3 object, the base64
 * fallback, and what the model sees are all the resized copy.
 */
import ImageCompressor from "compressorjs";
import commons from "../commons/commons";
import { commonStore } from "../store/commonStore";

let attachUid = 0;

export default {
  name: "TeammateAttachments",
  props: {
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // Compact mode hides the built-in bar and drag handling — the parent composer supplies the
    // "+" trigger and the drop zone and calls our public methods. Default keeps the standalone bar.
    compact: { type: Boolean, default: false },
    // Hard cap per file. Staged uploads go straight to S3, so this is no longer bound by
    // request-body size — the backend presigned limit is 500 MB; keep the UI well under it.
    maxFileSize: { type: Number, default: 100 * 1024 * 1024 },
    // Files up to this size also keep a base64 copy (thumbnail + inline fallback when the
    // staged upload fails). base64 inflates payloads ~33%, so cap it.
    inlineFallbackSize: { type: Number, default: 15 * 1024 * 1024 },
    // Images larger than this are resized (≤1200px) before staging — same threshold and
    // semantics as FTSComment/FTSFileUpload's autoCompress (0 disables).
    autoCompress: { type: Number, default: 500 * 1024 },
    // Workspace header for the upload API calls (same wsId the parent sends on /chat).
    wsId: { type: [String, Number], default: "" },
    // Chat session the upload belongs to — tagged onto the S3 object for attribution.
    sessionId: { type: String, default: "" },
  },
  data() {
    return { dragOver: false, error: "" };
  },
  computed: {
    totalBytes() {
      return this.value.reduce((sum, a) => sum + (a.size || 0), 0);
    },
  },
  methods: {
    /** Public: open the OS file picker (called by the composer's "+" button). */
    pickFiles() {
      if (this.disabled) return;
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onInputChange(evt) {
      this.addFiles(evt.target.files);
      evt.target.value = ""; // let the same file be re-selected later
    },
    // Built-in drag/drop for the standalone bar. Inert in compact mode — the parent composer is
    // the drop zone there, so we don't double-handle (or fight it for the dragOver outline).
    onDragOver(evt) {
      if (this.compact) return;
      evt.preventDefault();
      if (!this.disabled) this.dragOver = true;
    },
    onDragLeave() {
      if (this.compact) return;
      this.dragOver = false;
    },
    onDrop(evt) {
      if (this.compact) return;
      evt.preventDefault();
      this.dragOver = false;
      if (this.disabled) return;
      const dt = evt.dataTransfer;
      if (dt && dt.files && dt.files.length) this.addFiles(dt.files);
    },
    /** Public: accept pasted clipboard items (called by the parent's composer). Returns the count added. */
    addClipboardItems(items) {
      if (this.disabled || !items) return 0;
      const files = [];
      for (const it of Array.from(items)) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) this.addFiles(files);
      return files.length;
    },
    async addFiles(fileList) {
      this.error = "";
      const files = Array.from(fileList || []);
      const added = [];
      for (let file of files) {
        if (this.maxFileSize && file.size > this.maxFileSize) {
          this.error = `"${file.name}" is ${this.formatBytes(file.size)} — exceeds the ${this.formatBytes(this.maxFileSize)} limit.`;
          continue;
        }
        // Resize oversized bitmap images before staging, like the comment/file-upload fields
        // (≤1200px, EXIF orientation fixed). isImageFile skips gif/svg/webp — a canvas
        // re-encode would flatten animation/vector data. The compressed blob then feeds both
        // the S3 upload and the base64 fallback below.
        const srcName = file.name || "file";
        if (
          (file.type || "").indexOf("image/") === 0 &&
          commons.isImageFile(srcName) &&
          this.autoCompress > 0 &&
          this.autoCompress < file.size
        ) {
          file = await this.compressImage(file);
        }
        const att = {
          id: `att-${Date.now()}-${attachUid++}`,
          name: file.name || srcName,
          mimeType: file.type || "application/octet-stream",
          size: file.size || 0,
          isImage: (file.type || "").indexOf("image/") === 0,
          dataUrl: null,
          data: "",
          path: null,
          uploading: false,
          uploadError: "",
          _upload: null,
        };
        // Small files keep a base64 copy: thumbnail for images, inline fallback for the send
        // when the staged upload fails. Large files skip it (reading 100MB into a data URL
        // stalls the tab) — for them the staged upload is the only transport.
        if (file.size <= this.inlineFallbackSize) {
          try {
            const dataUrl = String(await this.readDataUrl(file));
            att.dataUrl = dataUrl;
            att.data = dataUrl.split(",")[1] || "";
          } catch (err) {
            this.error = `Could not read "${file.name}".`;
            continue;
          }
        }
        att._upload = this.uploadAttachment(att, file);
        added.push(att);
      }
      if (added.length) this.$emit("input", this.value.concat(added));
    },
    /**
     * Stage the file to S3 (request-upload-url → presigned PUT → confirm-upload) and turn the
     * attachment into a { name, path } ref. Never rejects: on failure the attachment keeps its
     * base64 fallback when it has one, otherwise the chip flips to the failed state and the
     * send drops it.
     */
    async uploadAttachment(att, file) {
      const client = commonStore.state && commonStore.state.client;
      if (!client) return;
      att.uploading = true;
      try {
        const cfg = this.wsId ? { headers: { wsId: this.wsId } } : {};
        const body = {
          fileName: att.name,
          contentType: att.mimeType,
          fileSize: att.size,
        };
        if (this.sessionId) body.sessionId = this.sessionId;
        const req = await client.post("/api/teammate/request-upload-url", body, cfg);
        const { uploadUrl, s3Key, filename } = req.data || {};
        if (!uploadUrl || !s3Key) throw new Error("no upload URL returned");

        // Raw fetch, not the API client: the presigned URL is self-authorizing and S3 rejects
        // requests carrying extra auth headers. Content-Type must match what was signed.
        const put = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": att.mimeType },
          body: file,
        });
        if (!put.ok) throw new Error(`S3 PUT failed (${put.status})`);

        // fileName echoes the server-sanitized name — it disambiguates the S3 key parse
        // when the file name itself contains dashes.
        const confirmBody = { s3Key, fileName: filename || att.name };
        if (this.sessionId) confirmBody.sessionId = this.sessionId;
        const conf = await client.post("/api/teammate/confirm-upload", confirmBody, cfg);
        const path = conf.data && conf.data.path;
        if (!path) throw new Error("confirm-upload returned no path");

        att.path = path;
        att.data = ""; // ref supersedes the base64 fallback — free the memory
      } catch (e) {
        // With a base64 fallback the send still works (inline), so stay quiet; without one
        // the chip shows the failure and the send will drop this attachment.
        if (!att.data) att.uploadError = "upload failed";
      } finally {
        att.uploading = false;
      }
    },
    /** Public: resolves once every staged upload has settled (called by the composer's send). */
    awaitUploads() {
      const pending = this.value.map((a) => a && a._upload).filter(Boolean);
      return pending.length ? Promise.all(pending) : Promise.resolve();
    },
    /**
     * Downscale an image with the same compressorjs settings the comment/file-upload fields
     * use. Resolves the original file when compression fails (corrupt image, canvas limits) —
     * unlike those fields we aren't squeezing into a request-body cap, so the full-size
     * original through the staged upload beats failing the attachment.
     */
    compressImage(file) {
      return new Promise((resolve) => {
        new ImageCompressor(file, {
          maxWidth: 1200,
          maxHeight: 1200,
          success: (compressed) => resolve(compressed),
          error: () => resolve(file),
        });
      });
    },
    readDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("read error"));
        reader.readAsDataURL(file);
      });
    },
    removeAt(i) {
      const next = this.value.slice();
      next.splice(i, 1);
      this.$emit("input", next);
      this.error = "";
    },
    clearAll() {
      if (this.disabled) return;
      this.$emit("input", []);
      this.error = "";
    },
    formatBytes(n) {
      const b = Number(n) || 0;
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
      return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.tm-attach {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
/* compact mode carries no bar, so drop its own column gap when it's only chips */
.tm-attach--compact { gap: 6px; }
.tm-attach--drag {
  outline: 2px dashed var(--fts-ai, #8b5cf6);
  outline-offset: 3px;
  border-radius: 8px;
  background: rgba(var(--fts-ai-rgb, 139, 92, 246), 0.05);
}
.tm-attach-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.tm-attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid var(--fts-border, #d1d5db);
  background: var(--fts-gray-25, #fff);
  color: var(--fts-text, #4b5563);
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}
.tm-attach-btn:hover:not(:disabled) { background: var(--fts-gray-100, #f3f4f6); }
.tm-attach-btn:disabled { color: var(--fts-gray-300, #d1d5db); cursor: not-allowed; }
.tm-attach-hint { font-size: 11px; color: var(--fts-gray-400, #9ca3af); }
.tm-attach-summary {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--fts-gray-500, #6b7280);
}
.tm-attach-clear {
  border: none;
  background: none;
  padding: 0;
  color: var(--fts-ai, #6d28d9);
  font-size: 11px;
  cursor: pointer;
}
.tm-attach-clear:hover:not(:disabled) { text-decoration: underline; }
.tm-attach-clear:disabled { color: var(--fts-gray-300, #c4b5fd); cursor: not-allowed; }
.tm-attach-file { display: none; }
.tm-attach-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tm-attach-item {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 240px;
  padding: 4px 6px 4px 4px;
  border: 1px solid var(--fts-border, #e5e7eb);
  border-radius: 8px;
  background: var(--fts-gray-25, #fff);
}
.tm-attach-thumb {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 6px;
  object-fit: cover;
  background: var(--fts-gray-100, #f3f4f6);
}
.tm-attach-ficon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: var(--fts-gray-100, #f3f4f6);
  color: var(--fts-gray-500, #6b7280);
  font-size: 14px;
}
.tm-attach-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tm-attach-name {
  max-width: 150px;
  font-size: 12px;
  color: var(--fts-text, #1f2328);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tm-attach-size { font-size: 10px; color: var(--fts-gray-400, #9ca3af); }
.tm-attach-item--error { border-color: var(--fts-danger, #dc2626); }
.tm-attach-item--error .tm-attach-name { color: var(--fts-danger, #b91c1c); }
.tm-attach-erricon { color: var(--fts-danger, #dc2626); }
.tm-attach-remove {
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 2px 4px;
  color: var(--fts-gray-400, #9ca3af);
  font-size: 12px;
  cursor: pointer;
}
.tm-attach-remove:hover:not(:disabled) { color: var(--fts-danger, #dc2626); }
.tm-attach-remove:disabled { color: var(--fts-gray-300, #d1d5db); cursor: not-allowed; }
.tm-attach-error { font-size: 12px; color: var(--fts-danger, #b91c1c); }
</style>
